import React, { useState } from "react";
import {
  Box,
  Card,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
  SelectChangeEvent,
} from "@mui/material";
import { Info, OpenInNew } from "@mui/icons-material";
import { ScholarshipDataCollection } from "../../api";
import { CategoryCard } from "../../components/CategoryContainer";
import { SurveyBody } from "../../components/SurveyBody";
import { PageHeader } from "../../components/PageHeader";
import { Button } from "../../components/Button";
import {
  filterScholarshipCollection,
  scholarshipCategories,
  ScholarshipCategory,
  sortScholarshipCollection,
} from "./scholarshipSortFilter";

function ResultsHeader({
  scholarshipCount,
  phoneNumber,
}: {
  scholarshipCount: number;
  phoneNumber?: string;
}) {
  return (
    <PageHeader
      title="Scholarship Results"
      body={[
        `Great news! We found ${scholarshipCount} scholarship${
          scholarshipCount > 1 || scholarshipCount === 0 ? "s" : ""
        } that you may be
        eligible for. To apply, simply click the button associated to each
        scholarship. ${
          phoneNumber
            ? `We’ve also sent a link to these results to ${phoneNumber}`
            : ""
        }`,
      ]}
    />
  );
}

interface ScholarshipAmountProps {
  scholarshipMaximumAward?: number | null;
  scholarshipMinimumAward?: number | null;
}

function ScholarshipAmount({
  scholarshipMinimumAward,
  scholarshipMaximumAward,
}: ScholarshipAmountProps) {
  const currencyFmter = Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
  });

  if (!scholarshipMinimumAward && scholarshipMaximumAward) {
    return (
      <Box display="inline">
        You could win:{" "}
        <Box display="inline" fontWeight={600}>
          {currencyFmter.format(scholarshipMaximumAward)}
        </Box>
      </Box>
    );
  }

  if (!scholarshipMaximumAward && scholarshipMinimumAward) {
    return (
      <Box display="inline">
        You could win:{" "}
        <Box display="inline" fontWeight={600}>
          {currencyFmter.format(scholarshipMinimumAward)}
        </Box>
      </Box>
    );
  }

  if (scholarshipMinimumAward && scholarshipMaximumAward) {
    return (
      <Box>
        You could win:{" "}
        <Box display="inline" fontWeight={600}>
          {currencyFmter.format(scholarshipMinimumAward)} -{" "}
          {currencyFmter.format(scholarshipMaximumAward)}
        </Box>
      </Box>
    );
  }

  return (
    <Box display="inline">
      You could win:{" "}
      <Box display="inline" fontWeight={600}>
        Not Specified
      </Box>
    </Box>
  );
}

/**
 * Displays a date in a user friendly format.
 * @prop label - Text to place before date
 * @prop inputString - String in the format %Y-%m-%d (strftime format), if this value cannot be parsed into a date the entire component will be hidden
 * @prop fallbackDisplay - String to display if there is no value in the input string, if not provided and there is no value for inputString the entire component will be hidden, will not be used if inputString is not formatted correctly
 */
function DateDisplay({
  label,
  inputString,
  fallbackDisplay,
}: {
  readonly label: string;
  readonly inputString: string | undefined | null;
  readonly fallbackDisplay?: string;
}) {
  // Check if data is provided
  if (!inputString && !fallbackDisplay) {
    // No data and no fallback don't render
    return null;
  }

  // Try to format date
  let formattedDate = fallbackDisplay;

  if (inputString) {
    const date = new Date(inputString);
    if (date.toString() === "Invalid Date") {
      // Date was not in a valid format, don't render
      return null;
    }

    formattedDate = `${
      date.getMonth() + 1
    }/${date.getDate()}/${date.getFullYear()}`;
  }

  return (
    <Box>
      {label}:{" "}
      <Box display="inline" fontWeight={600}>
        {formattedDate}
      </Box>
    </Box>
  );
}

function ResultCard({
  programName,
  blurb,
  scholarshipMaximumAward,
  scholarshipMinimumAward,
  openDate,
  closeDate,
  programURL,
  applicationURL,
  eligibilityCriteriaDescription,
}: ScholarshipDataCollection) {
  return (
    <CategoryCard>
      <Typography
        sx={{
          fontWeight: 600,
          fontSize: "1rem",
        }}
      >
        {programName}
      </Typography>
      <Typography
        sx={{
          fontWeight: 400,
          fontSize: "1rem",
        }}
      >
        {blurb || "Click the button below to learn more"}
      </Typography>
      <Box>
        <Box
          sx={{
            marginBottom: "0.5rem",
          }}
        >
          <ScholarshipAmount
            scholarshipMaximumAward={scholarshipMaximumAward}
            scholarshipMinimumAward={scholarshipMinimumAward}
          />
        </Box>
        <Box
          sx={{
            marginBottom: "0.5rem",
          }}
        >
          <DateDisplay label="Opening" inputString={openDate} />
        </Box>
        <Box>
          <DateDisplay
            label="Application Deadline"
            inputString={closeDate}
            fallbackDisplay="No deadline"
          />
        </Box>
      </Box>
      {(applicationURL || programURL) && (
        <Button
          variant="contained"
          href={applicationURL || programURL || ""}
          target="_blank"
          sx={{
            justifyContent: "flex-start",
            maxWidth: "14.688rem",
          }}
        >
          <Stack direction="row" alignItems="center" gap={1}>
            <OpenInNew />
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "1rem",
              }}
            >
              Apply for scholarship
            </Typography>
          </Stack>
        </Button>
      )}
      {eligibilityCriteriaDescription && (
        <Tooltip
          componentsProps={{
            tooltip: {
              sx: {
                background: "#475467",
                padding: "1rem",
              },
            },
          }}
          title={
            <Box>
              {eligibilityCriteriaDescription.split("\n").map((desc, i) => {
                if (desc.indexOf("-") === 0) {
                  // Convert faux bullet points into HTML bullet points
                  return (
                    <li
                      key={i}
                      style={{
                        listStylePosition: "outside",
                        marginLeft: "0.6rem",
                      }}
                    >
                      {desc.substring(1)}
                    </li>
                  );
                }

                return <>{desc}</>;
              })}
            </Box>
          }
          placement="top-start"
          enterTouchDelay={100}
        >
          <Stack
            direction="row"
            gap={1}
            sx={{
              color: "#092e63",
              userSelect: "none",
            }}
          >
            <Typography>Why did you match</Typography>
            <Info />
          </Stack>
        </Tooltip>
      )}
    </CategoryCard>
  );
}

// CONSTANTS

const ALL_SCHOLARSHIPS_CATEGORY = "All Scholarships";

// TYPES

interface ResultsProps {
  scholarships: ScholarshipDataCollection[];
}

// add in `ALL_SCHOLARSHIPS_CATEGORY` item to generate UI selectable categories
const selectCategories = [
  ALL_SCHOLARSHIPS_CATEGORY,
  ...scholarshipCategories,
] as const;

type SelectCategory = typeof selectCategories[number];

// TYPE GUARDS

const isSelectCategory = (str: string): str is SelectCategory => {
  const categories: string[] = selectCategories.map((value) => value);
  return categories.includes(str);
};

export function Results({ scholarships }: ResultsProps) {
  // STATE

  const [sortingCategory, setSortingCategory] =
    useState<SelectCategory>("Active");

  // EVENT HANDLERS

  const handleCategoryChange = (
    event: SelectChangeEvent<SelectCategory>
  ): void => {
    const { value } = event.target;
    isSelectCategory(value) && setSortingCategory(value);
  };

  // LOGIC

  const orderedScholarships = React.useMemo(() => {
    // filter into categories and sort within each for `sortingCategory` set to `ALL_SCHOLARSHIPS_CATEGORY`
    if (sortingCategory === ALL_SCHOLARSHIPS_CATEGORY) {
      // categories to filter by prior to sorting
      const allScholarshipsCategories: ScholarshipCategory[] = [
        "Active",
        "Coming Soon",
        "Expired",
      ];

      // iterate through each `allScholarshipsCategories` categories and filter, sort, and append the results
      return allScholarshipsCategories.reduce<ScholarshipDataCollection[]>(
        (sortFilteredScholarships, sortingCategory) => {
          const filteredScholarships = [...scholarships]
            .filter(filterScholarshipCollection[sortingCategory])
            .sort(sortScholarshipCollection[sortingCategory]);

          return [...sortFilteredScholarships, ...filteredScholarships];
        },
        []
      );

      // for all other `sortingCategory` cases, filter and sort within just that one category
    } else {
      return [...scholarships]
        .filter(filterScholarshipCollection[sortingCategory])
        .sort(sortScholarshipCollection[sortingCategory]);
    }
  }, [scholarships, sortingCategory]);

  // RENDER

  if (scholarships.length === 0) {
    return (
      <PageHeader
        title="Scholarship Results"
        body={[
          "We're sorry. There are currently no scholarships available that match your answers. Please try again soon - as more scholarships are added daily.",
        ]}
      />
    );
  }

  const CategorySelectItems = selectCategories.map((category, index) => (
    <MenuItem key={index} value={category}>
      {category}
    </MenuItem>
  ));

  const OrderedScholarships = orderedScholarships.map((scholarship, idx) => (
    <ResultCard
      {...scholarship}
      key={`${idx}-${scholarship?.programReferenceId}`}
    />
  ));

  const countMessage = `Viewing ${orderedScholarships.length} result${
    orderedScholarships.length > 1 ? "s" : ""
  }`;

  return (
    <>
      <ResultsHeader scholarshipCount={scholarships.length} />
      <Card
        sx={{
          display: "flex",
          flexDirection: "column",
          borderRadius: 2,
          padding: 2,
          marginBottom: 2,
          rowGap: 3,
        }}
      >
        <FormControl fullWidth>
          <InputLabel id="sort-by-select-label">Sort By</InputLabel>
          <Select
            labelId="sort-by-select-label"
            id="sort-by-select"
            value={sortingCategory}
            label="Sort By"
            onChange={handleCategoryChange}
          >
            {CategorySelectItems}
          </Select>
        </FormControl>
        <i>{countMessage}</i>
      </Card>
      <SurveyBody
        sx={{
          marginBottom: "10rem",
        }}
      >
        {OrderedScholarships}
      </SurveyBody>
    </>
  );
}
